/**
 * useSlotChangeFlow — la machine de « Modifier mes créneaux » → les props de `<SlotChangeFlow>`.
 *
 * Le parent dit quels cours de la semaine ne conviennent plus ; le serveur répond, pour ces cours,
 * par les places libres chez leurs tuteurs (une place par cours, et c'est fait tout de suite), ou
 * par une grille de la semaine où il a colorié les horaires de chaque tuteur — les horaires cochés
 * partent en proposition, un autre tuteur est cherché en même temps, et quand un tuteur garde un
 * autre cours de l'enfant le parent dit ce qu'il veut s'il ne confirme pas (le repli). Deux appels :
 *  - lecture : `getStudentSlotChange` (`GET api/v1/student-slot-change`) — à l'ouverture sans ids
 *    (les cours), puis avec les cours choisis (`live_session_ids[]` : places, grille, replis) ; de
 *    nouveau après une place refusée (elle vient d'être prise) ;
 *  - envoi : `submitStudentSlotChange` (`POST …`), `{ live_session_ids, seats }` ou
 *    `{ live_session_ids, slots, fallbacks }` (`{}` sans repli).
 * Aucune règle n'est refaite ici : les places, les couleurs, les replis et le nombre d'horaires
 * à cocher sont ceux du serveur ; la machine ne fait que les montrer et recueillir les choix.
 *
 * Rien ne part sans confirmation : « Confirmer votre choix ? » (`confirming`) dit ce qui va se
 * passer, « Modifier » la ferme sans rien effacer.
 *
 * Les deux lectures partagent une tranche : `useApiFetch` les borne par `scopeUrlParams`
 * (`live_session_ids`), une réponse pour d'autres cours n'est pas la nôtre. Le réducteur décide
 * quand lire (`coursesLoading`, `optionsLoading`) ; le crochet ne fait que suivre.
 *
 * **Un seul parcours tant qu'un envoi est en vol** (comme `useStayWithTutorFlow`) : fermer ne fait
 * que CACHER la feuille, la rouvrir la remontre telle quelle, et la réponse s'écrit dans la feuille
 * si elle est visible, dans le bandeau sinon.
 *
 * Amplitude : `parent_slot_change_confirmed` au succès, `{ outcome, courses_count, tutors_count }`.
 *
 * Pas de réducteur de données : la réponse ne sert qu'à cette feuille (patron B, comme les options
 * de « Garder son tuteur »). `onChanged` relit ce que le changement a bougé ailleurs.
 *
 * Le réducteur est pur et exporté : chaque transition se joue dans un test.
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useReducer } from "react"
import { useSelector } from "react-redux"

import { studentSlotChangeService } from "../../../../data/services/studentSlotChangeService"
import { type SnackbarProps, type SnackbarTone } from "../../../../design-system/molecules/Snackbar"
import { type SlotChangeFlowProps, type SlotChangeFlowStep } from "../../../../design-system/templates/SlotChangeFlow"
import {
    toUISlotChangeCourses,
    toUISlotChangeOptions,
    toUISlotChangeOutcome,
    toUISlotChangePending,
    type ApiSlotChangeOutcome,
    type ApiSlotChangePayload,
} from "../../adapters/parent/SlotChange/slotChangeAdapter"
import { childDisplayName } from "../formatters/childFormatter"
import { chosenSeat, enoughSlots, fallbacksComplete, seatsComplete } from "../formatters/slotChangeFormatter"
import {
    type UISlotChangeCourse,
    type UISlotChangeFallbackChoice,
    type UISlotChangeOptions,
    type UISlotChangeOutcome,
    type UISlotChangePending,
} from "../types"
import { apiErrorMessage } from "./apiErrorMessage"
import { useApiFetch } from "./useApiFetch"
import { useApiMutation } from "./useApiMutation"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

const COURSES_FALLBACK_ERROR = "Impossible de charger les cours. Veuillez réessayer."
const OPTIONS_FALLBACK_ERROR = "Impossible de charger les horaires. Veuillez réessayer."
const SUBMIT_FALLBACK_ERROR = "Impossible d'enregistrer votre choix. Veuillez réessayer."

export const SLOT_CHANGE_CONFIRMED_EVENT = "parent_slot_change_confirmed"

/** Les deux envois : une place par cours, ou des horaires proposés. */
export type SlotChangeBranch = "seat" | "offer"

export type SlotChangeFlowState = {
    visible: boolean
    step: SlotChangeFlowStep
    /** Vrai, l'étape attend la liste des cours : c'est ce qui fait partir la lecture. */
    coursesLoading: boolean
    courses: UISlotChangeCourse[]
    /** Un changement déjà en cours, pour le suivre : aucun cours ne se coche avant qu'il soit réglé. */
    pending: UISlotChangePending | null
    selectedCourseIds: number[]
    /** Vrai, les cours choisis attendent leurs places et leur grille : la lecture part. */
    optionsLoading: boolean
    options: UISlotChangeOptions | null
    /** Cours qui change → la place choisie pour lui. */
    seatChoices: Record<number, number>
    slotKeys: string[]
    fallbackChoices: Record<string, UISlotChangeFallbackChoice>
    /** La question « Confirmer votre choix ? » est ouverte, sur cet envoi. */
    confirming: SlotChangeBranch | null
    /** Vrai de l'envoi à sa réponse : fermer ne fait alors que cacher la feuille. */
    submitting: boolean
    error: string | null
    outcome: UISlotChangeOutcome | null
    /** Le bandeau du portail — l'issue d'un envoi dont la feuille était cachée. */
    notice: { message: string; tone: SnackbarTone } | null
    /** L'enfant du parcours, pris à l'ouverture. */
    childFirstname: string | null
}

export const initialSlotChangeFlowState: SlotChangeFlowState = {
    visible: false,
    step: "courses",
    coursesLoading: false,
    courses: [],
    pending: null,
    selectedCourseIds: [],
    optionsLoading: false,
    options: null,
    seatChoices: {},
    slotKeys: [],
    fallbackChoices: {},
    confirming: null,
    submitting: false,
    error: null,
    outcome: null,
    notice: null,
    childFirstname: null,
}

export type SlotChangeFlowAction =
    | { type: "OPEN"; childFirstname?: string | null }
    | {
          type: "COURSES_LOADED"
          courses: UISlotChangeCourse[]
          pending?: UISlotChangePending | null
      }
    | { type: "COURSES_FAILED"; message: string }
    | { type: "RETRY_COURSES" }
    | { type: "TOGGLE_COURSE"; liveSessionId: number }
    | { type: "CONTINUE_COURSES" }
    | { type: "OPTIONS_LOADED"; options: UISlotChangeOptions }
    | { type: "OPTIONS_FAILED"; message: string }
    | { type: "SELECT_SEAT"; courseLiveSessionId: number; liveSessionId: number }
    /** « Choisir … » : poser la question sur les places choisies. */
    | { type: "ASK_SEAT_CONFIRM" }
    /** « Ça ne nous va pas, voir d'autres horaires ». */
    | { type: "DECLINE_SEATS" }
    | { type: "TOGGLE_SLOT"; key: string }
    | { type: "CONTINUE_GRID" }
    | { type: "SELECT_FALLBACK"; key: string; choice: UISlotChangeFallbackChoice }
    | { type: "CONTINUE_FALLBACK" }
    /** « Modifier » : la question se ferme, les choix restent. */
    | { type: "CANCEL_CONFIRM" }
    | { type: "BACK" }
    | { type: "SUBMIT" }
    | { type: "SUBMIT_SUCCESS"; outcome: UISlotChangeOutcome }
    | { type: "SUBMIT_ERROR"; message: string }
    | { type: "CLOSE" }
    | { type: "DISMISS_NOTICE" }

export { enoughSlots, fallbacksComplete, seatsComplete }

/** Les replis déjà choisis, gardés tant que le repli existe ; aucun n'est coché d'avance. */
function fallbackChoicesFor(
    options: UISlotChangeOptions,
    previous: Record<string, UISlotChangeFallbackChoice>
): Record<string, UISlotChangeFallbackChoice> {
    return Object.fromEntries(
        options.fallbacks
            .filter((fallback) => previous[fallback.key] != null)
            .map((fallback) => [fallback.key, previous[fallback.key]!])
    )
}

/** Les places choisies encore offertes après une relecture. */
function seatChoicesStillOffered(
    choices: Record<number, number>,
    options: UISlotChangeOptions
): Record<number, number> {
    return Object.fromEntries(
        Object.entries(choices).filter(([courseId]) => chosenSeat(options.seats, choices, Number(courseId)) != null)
    )
}

/** Un horaire cochable : dans la grille, et pas un cours que l'enfant garde. */
function selectableSlot(options: UISlotChangeOptions | null, key: string): boolean {
    return (options?.grid.days ?? []).some((day) => day.slots.some((slot) => slot.key === key && !slot.course))
}

/** Ce que le bandeau dit quand la feuille était cachée à la réponse. */
function noticeFor(outcome: UISlotChangeOutcome): string {
    return outcome.kind === "seat" ? "Créneaux modifiés" : "Demande envoyée"
}

/** Le corps de l'envoi, pour la voie confirmée. */
export function slotChangeBody(state: SlotChangeFlowState, branch: SlotChangeBranch): Record<string, unknown> {
    const live_session_ids = state.selectedCourseIds
    if (branch === "seat") {
        const seats = Object.fromEntries(live_session_ids.map((id) => [id, state.seatChoices[id]]))
        return { live_session_ids, seats }
    }
    // `fallbackChoices` tient un choix par repli offert, ni plus ni moins (`fallbackChoicesFor`, `fallbacksComplete`).
    return { live_session_ids, slots: state.slotKeys, fallbacks: state.fallbackChoices }
}

/** Les tuteurs distincts des cours choisis — ce que compte Amplitude. */
export function selectedTutorsCount(state: Pick<SlotChangeFlowState, "courses" | "selectedCourseIds">): number {
    const ids = state.courses
        .filter((course) => state.selectedCourseIds.includes(course.liveSessionId) && course.tutor != null)
        .map((course) => String(course.tutor?.id))
    return new Set(ids).size
}

/** La question peut-elle partir — ni accusé déjà là, ni envoi en vol, ni lecture en cours ? */
function canSend(state: SlotChangeFlowState): boolean {
    if (state.confirming == null || state.outcome != null || state.submitting || state.optionsLoading) return false
    return state.confirming === "seat" ? seatsComplete(state) : enoughSlots(state) && fallbacksComplete(state)
}

/** Les gestes d'une étape ne passent que sur elle, feuille ouverte, sans envoi ni question en cours. */
function onStep(state: SlotChangeFlowState, step: SlotChangeFlowStep): boolean {
    return state.visible && state.step === step && !state.submitting && state.confirming == null
}

export function slotChangeFlowReducer(state: SlotChangeFlowState, action: SlotChangeFlowAction): SlotChangeFlowState {
    switch (action.type) {
        case "OPEN":
            // Un envoi en vol : c'est SON parcours qu'on remontre, tel quel.
            if (state.submitting) return { ...state, visible: true }
            return {
                ...initialSlotChangeFlowState,
                notice: state.notice,
                visible: true,
                coursesLoading: true,
                childFirstname: action.childFirstname ?? null,
            }

        case "COURSES_LOADED": {
            if (!state.coursesLoading) return state
            const known = new Set(action.courses.map((course) => course.liveSessionId))
            return {
                ...state,
                coursesLoading: false,
                courses: action.courses,
                pending: action.pending ?? null,
                selectedCourseIds: action.pending ? [] : state.selectedCourseIds.filter((id) => known.has(id)),
            }
        }

        case "COURSES_FAILED":
            if (!state.coursesLoading) return state
            return { ...state, coursesLoading: false, error: action.message }

        case "RETRY_COURSES":
            if (!state.visible || state.coursesLoading || state.step !== "courses") return state
            return { ...state, coursesLoading: true, error: null }

        case "TOGGLE_COURSE": {
            if (!onStep(state, "courses") || state.optionsLoading || state.pending != null) return state
            if (!state.courses.some((course) => course.liveSessionId === action.liveSessionId)) return state
            const selected = state.selectedCourseIds.includes(action.liveSessionId)
            return {
                ...state,
                selectedCourseIds: selected
                    ? state.selectedCourseIds.filter((id) => id !== action.liveSessionId)
                    : [...state.selectedCourseIds, action.liveSessionId],
                error: null,
            }
        }

        case "CONTINUE_COURSES":
            if (!onStep(state, "courses") || state.optionsLoading || state.selectedCourseIds.length === 0) {
                return state
            }
            return { ...state, optionsLoading: true, error: null }

        case "OPTIONS_LOADED": {
            if (!state.optionsLoading) return state
            const { options } = action
            const nextStep: SlotChangeFlowStep = options.seats.length > 0 ? "seats" : "grid"
            if (state.step === "courses") {
                return {
                    ...state,
                    optionsLoading: false,
                    options,
                    step: nextStep,
                    seatChoices: {},
                    slotKeys: [],
                    fallbackChoices: fallbackChoicesFor(options, {}),
                }
            }
            // Relecture après une place refusée : le message reste (il explique pourquoi les places
            // ont changé), et seuls les choix encore offerts survivent.
            return {
                ...state,
                optionsLoading: false,
                options,
                step: nextStep,
                seatChoices: seatChoicesStillOffered(state.seatChoices, options),
                slotKeys: state.slotKeys.filter((key) => selectableSlot(options, key)),
                fallbackChoices: fallbackChoicesFor(options, state.fallbackChoices),
            }
        }

        case "OPTIONS_FAILED":
            if (!state.optionsLoading) return state
            return { ...state, optionsLoading: false, error: action.message }

        case "SELECT_SEAT": {
            if (!onStep(state, "seats") || state.optionsLoading) return state
            // Un élève n'a qu'un siège par live : la place prise pour ce cours se retire à un autre qui l'avait.
            const others = Object.fromEntries(
                Object.entries(state.seatChoices).filter(([, liveSessionId]) => liveSessionId !== action.liveSessionId)
            )
            const seatChoices = { ...others, [action.courseLiveSessionId]: action.liveSessionId }
            const offered = chosenSeat(state.options?.seats ?? [], seatChoices, action.courseLiveSessionId) != null
            if (!offered || !state.selectedCourseIds.includes(action.courseLiveSessionId)) return state
            return { ...state, seatChoices, error: null }
        }

        case "ASK_SEAT_CONFIRM":
            if (!onStep(state, "seats") || state.optionsLoading || !seatsComplete(state)) return state
            return { ...state, confirming: "seat" }

        case "DECLINE_SEATS":
            if (!onStep(state, "seats") || state.optionsLoading) return state
            return { ...state, step: "grid", error: null }

        case "TOGGLE_SLOT": {
            if (!onStep(state, "grid") || !selectableSlot(state.options, action.key)) return state
            const checked = state.slotKeys.includes(action.key)
            return {
                ...state,
                slotKeys: checked
                    ? state.slotKeys.filter((key) => key !== action.key)
                    : [...state.slotKeys, action.key],
                error: null,
            }
        }

        case "CONTINUE_GRID":
            if (!onStep(state, "grid") || !enoughSlots(state)) return state
            if ((state.options?.fallbacks.length ?? 0) > 0) return { ...state, step: "fallback", error: null }
            return { ...state, confirming: "offer" }

        case "SELECT_FALLBACK":
            if (!onStep(state, "fallback")) return state
            if (!(state.options?.fallbacks ?? []).some((fallback) => fallback.key === action.key)) return state
            return { ...state, fallbackChoices: { ...state.fallbackChoices, [action.key]: action.choice } }

        case "CONTINUE_FALLBACK":
            if (!onStep(state, "fallback") || !enoughSlots(state) || !fallbacksComplete(state)) return state
            return { ...state, confirming: "offer" }

        case "CANCEL_CONFIRM":
            // Pendant l'envoi, la question attend sa réponse.
            if (state.confirming == null || state.submitting) return state
            return { ...state, confirming: null }

        case "BACK": {
            if (!state.visible || state.submitting || state.confirming != null || state.outcome != null) return state
            const toCourses = { ...state, step: "courses" as const, options: null, optionsLoading: false, error: null }
            switch (state.step) {
                case "seats":
                    return { ...toCourses, seatChoices: {} }
                case "grid":
                    if ((state.options?.seats.length ?? 0) > 0) return { ...state, step: "seats", error: null }
                    return { ...toCourses, slotKeys: [] }
                case "fallback":
                    return { ...state, step: "grid", error: null }
                default:
                    return state
            }
        }

        case "SUBMIT":
            if (!canSend(state)) return state
            return { ...state, submitting: true, error: null }

        case "SUBMIT_SUCCESS":
            if (!state.submitting) return state
            if (!state.visible) {
                return {
                    ...initialSlotChangeFlowState,
                    notice: { message: noticeFor(action.outcome), tone: "success" },
                }
            }
            return {
                ...state,
                submitting: false,
                confirming: null,
                error: null,
                step: "done",
                outcome: action.outcome,
            }

        case "SUBMIT_ERROR": {
            if (!state.submitting) return state
            if (!state.visible) {
                return { ...initialSlotChangeFlowState, notice: { message: action.message, tone: "danger" } }
            }
            const refused = { ...state, submitting: false, confirming: null, error: action.message }
            // Une place vient d'être prise : retour aux places, relues, le message à l'écran.
            if (state.confirming === "seat") return { ...refused, step: "seats", optionsLoading: true }
            return refused
        }

        case "CLOSE":
            // Pendant l'envoi, la feuille se cache seulement : la réponse doit retrouver son parcours.
            if (state.submitting) return { ...state, visible: false }
            return { ...initialSlotChangeFlowState, notice: state.notice }

        case "DISMISS_NOTICE":
            return state.notice == null ? state : { ...state, notice: null }

        default:
            return state
    }
}

export type SlotChangeFlowConfig = {
    /** Prénom de l'enfant — l'écran le connaît déjà. */
    childFirstname?: string | null
    /** Rejoué au succès : relire ce que le changement a bougé ailleurs. */
    onChanged?: () => void
    /** Place à réserver sous le bandeau d'accusé — la hauteur de la barre d'onglets. */
    snackbarBottomOffset?: number
    /**
     * Ouvre « Décaler une séance » — l'autre geste, pour une absence d'une fois. La feuille se ferme
     * d'abord. Absent : la phrase s'écrit sans lien.
     */
    onMoveSession?: () => void
    /**
     * « Pour changer de tuteur, écrivez-nous » : la messagerie, le message déjà préparé. La feuille se ferme
     * d'abord. Absent : pas de phrase.
     */
    onAskTutorChange?: () => void
}

export type SlotChangeFlowAdapter = {
    open: () => void
    flowProps: SlotChangeFlowProps
    notice: SnackbarProps
}

export function useSlotChangeFlow(config: SlotChangeFlowConfig = {}): SlotChangeFlowAdapter {
    const { childFirstname, onChanged, snackbarBottomOffset = 0, onMoveSession, onAskTutorChange } = config
    const [state, dispatchUi] = useReducer(slotChangeFlowReducer, initialSlotChangeFlowState)
    // Stable : le bandeau réarme son minuteur de retrait à chaque nouveau `onDismiss`.
    const dismissNotice = useCallback(() => dispatchUi({ type: "DISMISS_NOTICE" }), [])

    const student = useSelector(selectDisplayedStudent)

    // ── Lectures ─────────────────────────────────────────────────────────────────────────────

    const { fetchWith: read, cancel: cancelRead } = useApiFetch<ApiSlotChangePayload>({
        service: studentSlotChangeService.getStudentSlotChange,
        fetchOnMount: false,
        scopeUrlParams: ["live_session_ids"],
        idleOnError: true,
        onSuccess: (payload) => {
            if (state.coursesLoading) {
                dispatchUi({
                    type: "COURSES_LOADED",
                    courses: toUISlotChangeCourses(payload?.courses),
                    pending: toUISlotChangePending(payload?.pending),
                })
            } else if (state.optionsLoading) {
                dispatchUi({ type: "OPTIONS_LOADED", options: toUISlotChangeOptions(payload) })
            }
        },
        onError: (error) => {
            if (state.coursesLoading) {
                dispatchUi({ type: "COURSES_FAILED", message: apiErrorMessage(error, COURSES_FALLBACK_ERROR) })
            } else if (state.optionsLoading) {
                dispatchUi({ type: "OPTIONS_FAILED", message: apiErrorMessage(error, OPTIONS_FALLBACK_ERROR) })
            }
        },
    })

    // Le réducteur décide quand lire ; dès qu'il n'attend plus (réponse lue, fermeture), la
    // lecture en cours est coupée.
    const coursesWanted = state.coursesLoading
    useEffect(() => {
        if (!coursesWanted) return
        read({})
        return cancelRead
    }, [coursesWanted, read, cancelRead])

    const optionsFor = state.optionsLoading ? state.selectedCourseIds : null
    useEffect(() => {
        if (optionsFor == null) return
        read({ live_session_ids: optionsFor })
        return cancelRead
    }, [optionsFor, read, cancelRead])

    // ── Envoi ────────────────────────────────────────────────────────────────────────────────

    const { start } = useApiMutation({
        service: studentSlotChangeService.submitStudentSlotChange,
        fallback: SUBMIT_FALLBACK_ERROR,
        skipWhilePending: true,
        onSuccess: (data) => {
            const outcome = toUISlotChangeOutcome(data as ApiSlotChangeOutcome | null)
            if (outcome == null) {
                dispatchUi({ type: "SUBMIT_ERROR", message: SUBMIT_FALLBACK_ERROR })
                return
            }
            amplitude.track(SLOT_CHANGE_CONFIRMED_EVENT, {
                outcome: outcome.kind,
                courses_count: state.selectedCourseIds.length,
                tutors_count: selectedTutorsCount(state),
            })
            onChanged?.()
            dispatchUi({ type: "SUBMIT_SUCCESS", outcome })
        },
        onError: (message) => dispatchUi({ type: "SUBMIT_ERROR", message }),
    })

    // ── Gestes ───────────────────────────────────────────────────────────────────────────────

    const open = useCallback(
        () => dispatchUi({ type: "OPEN", childFirstname: childFirstname ?? null }),
        [childFirstname]
    )
    const close = useCallback(() => dispatchUi({ type: "CLOSE" }), [])

    const confirm = useCallback(() => {
        if (state.confirming == null || !canSend(state)) return
        if (start({}, slotChangeBody(state, state.confirming))) dispatchUi({ type: "SUBMIT" })
    }, [state, start])

    const back = state.step === "courses" || state.step === "done" ? undefined : () => dispatchUi({ type: "BACK" })
    const moveSession = useCallback(() => {
        dispatchUi({ type: "CLOSE" })
        onMoveSession?.()
    }, [onMoveSession])
    const askTutorChange = useCallback(() => {
        dispatchUi({ type: "CLOSE" })
        onAskTutorChange?.()
    }, [onAskTutorChange])

    return {
        open,
        flowProps: {
            visible: state.visible,
            onClose: close,
            onBack: back,
            step: state.step,
            childFirstname: childDisplayName(
                state.visible || state.submitting ? state.childFirstname : childFirstname,
                student
            ),
            courses: state.courses,
            coursesPending: state.pending,
            coursesLoading: state.coursesLoading,
            onRetryCourses: () => dispatchUi({ type: "RETRY_COURSES" }),
            selectedCourseIds: state.selectedCourseIds,
            onToggleCourse: (liveSessionId) => dispatchUi({ type: "TOGGLE_COURSE", liveSessionId }),
            onContinueCourses: () => dispatchUi({ type: "CONTINUE_COURSES" }),
            continueLoading: state.optionsLoading && state.step === "courses",
            onMoveSession: onMoveSession != null ? moveSession : undefined,
            onAskTutorChange: onAskTutorChange != null ? askTutorChange : undefined,
            options: state.options,
            optionsLoading: state.optionsLoading,
            seatChoices: state.seatChoices,
            onSelectSeat: (seat) =>
                dispatchUi({
                    type: "SELECT_SEAT",
                    courseLiveSessionId: seat.courseLiveSessionId,
                    liveSessionId: seat.liveSessionId,
                }),
            onAskSeatConfirm: () => dispatchUi({ type: "ASK_SEAT_CONFIRM" }),
            onDeclineSeats: () => dispatchUi({ type: "DECLINE_SEATS" }),
            selectedSlotKeys: state.slotKeys,
            onToggleSlot: (key) => dispatchUi({ type: "TOGGLE_SLOT", key }),
            onContinueGrid: () => dispatchUi({ type: "CONTINUE_GRID" }),
            fallbackChoices: state.fallbackChoices,
            onSelectFallback: (key, choice) => dispatchUi({ type: "SELECT_FALLBACK", key, choice }),
            onContinueFallback: () => dispatchUi({ type: "CONTINUE_FALLBACK" }),
            confirming: state.confirming,
            onCancelConfirm: () => dispatchUi({ type: "CANCEL_CONFIRM" }),
            onConfirm: confirm,
            confirmLoading: state.submitting,
            outcome: state.outcome,
            errorMessage: state.error ?? undefined,
        },
        notice: {
            visible: state.notice != null,
            message: state.notice?.message ?? "",
            tone: state.notice?.tone ?? "success",
            onDismiss: dismissNotice,
            bottomOffset: snackbarBottomOffset,
        },
    }
}
